import type { CloudCostLine, CloudCostReport } from "@hasheeai/types";
import { Fragment, useState } from "react";
import { t } from "../../i18n";
import * as s from "../../styles/shared";
import { CostHelp } from "./CloudCostHelp";
import { CostTable } from "./CloudCostUsage";
import { costMoney } from "./cloud-cost-data";
import { costServiceLabel } from "./cloud-cost-labels";
import { datesInMonth, isDaily } from "./cloud-cost-model";
import { costCell } from "./cloud-cost-table-model";
const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  textAlign: "left",
};
const cellStyle: React.CSSProperties = {
  padding: "12px 8px",
  borderTop: "1px solid var(--color-border)",
};
export function FixedCostTable({
  report,
}: { report: CloudCostReport }): React.JSX.Element {
  return (
    <section style={s.card}>
      <h3 style={{ marginTop: 0 }}>
        {t("cost.fixed_short")}
        <CostHelp title={t("cost.fixed_short")}>
          <p>{t("cost.fixed_cycle_rule")}</p>
        </CostHelp>
      </h3>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th>{t("cost.provider")}</th>
            <th>{t("cost.plan_short")}</th>
            <th>{t("cost.monthly_short")}</th>
          </tr>
        </thead>
        <tbody>
          {(["neon", "cloudflare"] as const).map((provider) => (
            <FixedRow key={provider} provider={provider} report={report} />
          ))}
        </tbody>
      </table>
    </section>
  );
}
export function DailyCostTable({
  report,
  today,
}: { report: CloudCostReport; today: string }): React.JSX.Element {
  const [day, setDay] = useState("");
  const dates = datesInMonth(report.month, today).reverse();
  return (
    <section style={s.card}>
      <h3 style={{ marginTop: 0 }}>
        {t("cost.daily_short")}
        <CostHelp title={t("cost.daily_short")}>
          <p>{t("cost.rated_help")}</p>
        </CostHelp>
      </h3>
      <div style={{ overflow: "auto", maxHeight: 520 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th>{t("cost.date")}</th>
              <th>Neon</th>
              <th>Cloudflare</th>
              <th>{t("cost.total_short")}</th>
            </tr>
          </thead>
          <tbody>
            {dates.map((date) => (
              <DailyRow
                key={date}
                date={date}
                day={day}
                setDay={setDay}
                report={report}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function AmountCell({
  lines,
  requireBoth = false,
}: { lines: CloudCostLine[]; requireBoth?: boolean }): React.JSX.Element {
  const value = costCell(lines, requireBoth);
  return (
    <td style={cellStyle}>
      {value.amount === null ? "—" : costMoney(value.amount)}
      {value.partial && (
        <CostHelp title={t("cost.partial_short")}>
          <p>{t("cost.partial_help")}</p>
        </CostHelp>
      )}
    </td>
  );
}

function FixedRow({
  provider,
  report,
}: {
  provider: "neon" | "cloudflare";
  report: CloudCostReport;
}): React.JSX.Element {
  const rows =
    report.fixed_costs?.filter((row) => row.provider === provider) ?? [];
  return (
    <tr key={provider}>
      <td style={cellStyle}>{provider === "neon" ? "Neon" : "Cloudflare"}</td>
      <td style={cellStyle}>
        {rows.length
          ? rows
              .map(
                (row) =>
                  `${costServiceLabel(row.service)}${rows.length > 1 ? ` ${costMoney(row.monthly_amount)}` : ""}`,
              )
              .join(" + ")
          : "—"}
      </td>
      <td style={cellStyle}>
        {rows.length
          ? costMoney(rows.reduce((sum, row) => sum + row.monthly_amount, 0))
          : "—"}
      </td>
    </tr>
  );
}

function DailyRow({
  date,
  day,
  setDay,
  report,
}: {
  date: string;
  day: string;
  setDay: (day: string) => void;
  report: CloudCostReport;
}): React.JSX.Element {
  const lines = report.lines.filter(
    (line) => isDaily(line) && line.start.startsWith(date),
  );
  return (
    <Fragment key={date}>
      <tr>
        <td style={cellStyle}>
          <button
            type="button"
            style={dateButtonStyle}
            aria-expanded={day === date}
            onClick={() => setDay(day === date ? "" : date)}
          >
            {date.slice(5)} {day === date ? "▾" : "▸"}
          </button>
        </td>
        <AmountCell lines={lines.filter((line) => line.provider === "neon")} />
        <AmountCell
          lines={lines.filter((line) => line.provider === "cloudflare")}
        />
        <AmountCell lines={lines} requireBoth />
      </tr>
      {day === date && (
        <tr>
          <td colSpan={4}>
            <CostTable lines={lines} report={report} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

const dateButtonStyle: React.CSSProperties = {
  border: 0,
  background: "none",
  color: "inherit",
  cursor: "pointer",
  padding: 0,
};
